<template>
  <v-window v-model="tabModel" class="mobile-home-view" :touch="touchHandlers">
    <v-window-item :value="0">
      <Landing />
    </v-window-item>
    <v-window-item :value="1">
      <TeamsPage />
    </v-window-item>
    <v-window-item :value="2">
      <LeaguesPage />
    </v-window-item>
  </v-window>
</template>

<script setup lang="ts">
/**
 * MobileHomeView – unified mobile container for the 3 main tabs.
 *
 * Uses Vuetify v-window for smooth horizontal slide transitions.
 * v-window-item lazy-renders each tab on first visit and then keeps it alive
 * (v-show internally), so data/state is preserved across tab switches.
 *
 * QuickActionsMenu stays inside each page component. During v-window's brief
 * ~300 ms slide animation, fixed-positioned elements follow the panel, which
 * gives a natural "page slide" feel identical to native iOS/Android tabs.
 *
 * GameDayBanner lives in App.vue (above this component) and is never affected.
 */
import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNavTransition, TAB_PATHS } from '@/composables/useNavTransition'

import Landing from '@/components/Landing.vue'
import TeamsPage from '@/pages/teams/index.vue'
import LeaguesPage from '@/pages/leagues/index.vue'

const { activeTab, setTab, initFromPath, isMainRoute } = useNavTransition()
const route = useRoute()
const router = useRouter()

// ─── Initialize from current route on mount ────────────────────────────
onMounted(() => {
  initFromPath(route.path)
})

// ─── Sync tab when navigating back to a main route from a detail page ──
watch(
  () => route.path,
  (path) => {
    if (isMainRoute(path)) {
      initFromPath(path)
    }
  },
)

// ─── v-model bridge for v-window ────────────────────────────────────────
// setTab updates shared composable state, then we sync the URL.
const tabModel = computed({
  get: () => activeTab.value,
  set: (val: number) => {
    setTab(val)
    router.replace(TAB_PATHS[val]).catch(() => {})
  },
})

// ─── Swipe / touch support ──────────────────────────────────────────────
const touchHandlers = {
  left: () => {
    if (activeTab.value < 2) tabModel.value = activeTab.value + 1
  },
  right: () => {
    if (activeTab.value > 0) tabModel.value = activeTab.value - 1
  },
}
</script>

<style scoped>
.mobile-home-view {
  overflow-x: hidden;
  width: 100%;
  min-height: 100vh;
  position: relative;
}

/* Potlačit pozadí uvnitř v-window-items – řeší "uskakování" pozadí */
.mobile-home-view :deep(.background-wrapper) {
  background: transparent !important;
  min-height: auto !important;
}

/* Zachovat mobile gradient ::before/::after ale na celkovém wrapperu, ne per-tab */
.mobile-home-view :deep(.background-wrapper)::before,
.mobile-home-view :deep(.background-wrapper)::after {
  display: none !important;
}
</style>
